import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import "@testing-library/jest-dom";

import { HeartbeatEditor } from "./HeartbeatEditor";
import { heartbeatApi } from "../services/heartbeats";

jest.mock("../../../utils/useAuthFetch", () => ({
  useAuthFetch: jest.fn(() => jest.fn()),
}));

jest.mock("../utils/heartbeatRecurrence", () => {
  const actual = jest.requireActual("../utils/heartbeatRecurrence");
  return {
    ...actual,
    getBrowserTimezone: jest.fn(() => "Europe/Stockholm"),
  };
});

function buildTask() {
  return {
    task_id: "hb-1",
    kind: "cron" as const,
    status: "pending",
    next_fire_at: "2026-04-22T12:17:00Z",
    prompt: "Ping the team",
    conversation_id: 123,
  };
}

function buildDetail(cronExpression = "0 9 * * *") {
  return {
    ...buildTask(),
    cron_expression: cronExpression,
    timezone: "UTC",
    agent_model: "gpt-test",
  };
}

describe("HeartbeatEditor", () => {
  beforeEach(() => {
    jest.clearAllMocks();
    window.history.pushState({}, "", "/chat");
    jest.spyOn(heartbeatApi, "get").mockResolvedValue(buildDetail());
  });

  it("reveals the custom cron controls only in debug mode", async () => {
    window.history.pushState({}, "", "/chat?debug=1");

    render(
      <HeartbeatEditor
        open={true}
        workspaceId="ws-1"
        task={buildTask()}
        onSave={jest.fn().mockResolvedValue(undefined)}
        onClose={jest.fn()}
      />,
    );

    await waitFor(() => expect(heartbeatApi.get).toHaveBeenCalled());

    expect(
      screen.getByText(
        "Updates this heartbeat in place. The task id stays the same.",
      ),
    ).toBeInTheDocument();
    expect(screen.getByText("Custom cron")).toBeInTheDocument();
    fireEvent.click(screen.getByLabelText("Custom cron"));
    expect(
      screen.getByTestId("heartbeat-editor-custom-cron"),
    ).toBeInTheDocument();
  });

  it("hides the custom cron controls outside debug mode", async () => {
    render(
      <HeartbeatEditor
        open={true}
        workspaceId="ws-1"
        task={buildTask()}
        onSave={jest.fn().mockResolvedValue(undefined)}
        onClose={jest.fn()}
      />,
    );

    await waitFor(() => expect(heartbeatApi.get).toHaveBeenCalled());

    expect(screen.queryByText("Custom cron")).not.toBeInTheDocument();
    expect(
      screen.queryByTestId("heartbeat-editor-custom-cron"),
    ).not.toBeInTheDocument();
  });

  it("stops loading and offers retry when the detail request aborts", async () => {
    const abortError = new DOMException(
      "signal is aborted without reason",
      "AbortError",
    );
    jest
      .spyOn(heartbeatApi, "get")
      .mockRejectedValueOnce(abortError)
      .mockResolvedValueOnce(buildDetail());

    render(
      <HeartbeatEditor
        open={true}
        workspaceId="ws-1"
        task={buildTask()}
        onSave={jest.fn().mockResolvedValue(undefined)}
        onClose={jest.fn()}
      />,
    );

    expect(screen.getByText("Loading heartbeat details…")).toBeInTheDocument();

    await waitFor(() =>
      expect(
        screen.queryByText("Loading heartbeat details…"),
      ).not.toBeInTheDocument(),
    );
    expect(
      screen.getByText(
        "The heartbeat details request was interrupted. Retry loading details before saving.",
      ),
    ).toBeInTheDocument();
    expect(screen.getByTestId("heartbeat-editor-save")).toBeDisabled();

    fireEvent.click(
      screen.getByRole("button", { name: "Retry loading details" }),
    );

    await waitFor(() => expect(heartbeatApi.get).toHaveBeenCalledTimes(2));
    await waitFor(() =>
      expect(screen.getByTestId("heartbeat-editor-save")).not.toBeDisabled(),
    );
  });
});
